/**
 * FalGeneratingPlayer — the premium manual-mode "your video is rendering" screen.
 * Unlike the agent GeneratingPlayer, this is driven by REAL fal queue status
 * (polled via /v2-fal-status): queued (with position) → in progress → done.
 *
 * Progress: fal doesn't give a clean percent, so we use the scraped log percent
 * when present, otherwise a smooth time-based estimate (manual fal videos run far
 * shorter than HeyGen — a flat ~75s estimate, NOT the 14× agent formula), capped
 * at 95% until fal reports the render is done (then 99%). The parent swaps this
 * out for the finished video once the order completes.
 *
 * No blurred backdrop here (premium): just a clean branded gradient.
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { cn } from '@/v2/utils';
import { v2Service } from '@/api/v2Service';

// Per-model render-time estimate (seconds), used ONLY as the fallback pace when
// fal isn't emitting a real percent in its logs. Calibrated from a live timing
// sweep (one t2v job per model at default settings, June 2026): measured
// submit→COMPLETED wall times ranged ~30s (Seedance Fast / PixVerse) to ~540s
// (Pika) with a ~150s median — so a single flat number was always wrong for
// half the lineup. These are deliberately rounded a touch ABOVE the measured
// sample so the bar doesn't slam to its 95% cap and sit there: real fal queue
// time varies a lot by load, and we'd rather the bar still be climbing when the
// video lands than frozen. Matched by substring, most-specific first (mirrors
// the backend credit_rate ordering).
function estimateSec(model?: string): number {
  const m = (model || '').toLowerCase();
  if (!m) return 120;
  // Seedance 2.5 — newest SOTA; new + queue-heavy like 2.0. MUST be before generic 'seedance'.
  if (m.includes('seedance-2.5')) return 500;
  // Seedance 2.0 — brand-new SOTA; measured 487s for a 4s clip @720p (slow + queue-heavy).
  // MUST be before the generic 'seedance' check below. Loader still prefers fal's real percent.
  if (m.includes('seedance-2.0')) return 500;
  // ── Models wired from V1 (live fal sweep, June 2026: t2v in parallel at 4–5s;
  //    padded up for the longer default durations users actually pick + queue swing) ──
  // Veo ids now run on Gemini Omni Flash (fast tier + reference) or H3 Max (full + extend).
  if (m.includes('veo3.1/fast') || m.includes('veo3.1/reference') || m.includes('gemini-omni-flash')) return 60;
  if (m.includes('veo3.1') || m.includes('h3-max')) return 90;
  if (m.includes('sora-2') && m.includes('pro')) return 200; // 130s @4s; scales hard w/ duration (→12s)
  if (m.includes('sora-2')) return 130;               // base, 720p (71s @4s)
  if (m.includes('grok')) return 45;                  // 29s @5s — fast, ≤10s
  if (m.includes('wan/v2.6')) return 90;              // 68s @5s
  if (m.includes('kling-video/o3')) return 150;       // Kling 3.0 (106s @5s; same engine as v3)
  // Fast tier (~30–50s measured)
  if (m.includes('seedance/v1/pro/fast')) return 45;
  if (m.includes('pixverse')) return 55;            // v5 + transition
  if (m.includes('ray-2-flash')) return 55;
  if (m.includes('wan/v2.2')) return 70;
  // Medium (~70–180s measured)
  if (m.includes('seedance')) return 90;            // pro (non-fast)
  if (m.includes('v2.5-turbo')) return 100;         // kling 2.5 turbo
  if (m.includes('kling-video/v2.1/master')) return 170;
  if (m.includes('kling-video/v2.1')) return 140;   // standard tier
  if (m.includes('kling-video/v3')) return 150;       // Kling 3.0 (102s @5s)
  if (m.includes('wan-25')) return 150;
  if (m.includes('hailuo-02/pro') || m.includes('hailuo-2.3/pro')) return 180;
  // Slow (~270–360s measured)
  if (m.includes('vidu/q1')) return 270;
  if (m.includes('cogvideox')) return 300;
  if (m.includes('ray-2')) return 330;              // Luma Ray 2 (non-flash)
  if (m.includes('hailuo-02/standard') || m.includes('hailuo-2.3')) return 330;
  // Very slow (~420s+ measured)
  if (m.includes('marey')) return 420;
  if (m.includes('pika/v2.2')) return 480;
  if (m.includes('hunyuan')) return 480;
  if (m.includes('ltx-2')) return 120;
  return 120; // sensible default (median sample ≈ 150s)
}

export function FalGeneratingPlayer({ userId, orderId, startedAt, falModel }: { userId: string; orderId: string; startedAt?: string; falModel?: string }) {
  const { t } = useTranslation('v2');
  const mounted = useRef(Date.now());
  const [, force] = useState(0);
  const [status, setStatus] = useState<'queued' | 'in_progress' | 'rendered' | 'done' | 'unknown'>('unknown');
  const [queuePos, setQueuePos] = useState<number | null>(null);
  const [falPct, setFalPct] = useState<number | null>(null);

  // Smooth ticking for the time-based estimate.
  useEffect(() => {
    const t = setInterval(() => force((n) => n + 1), 250);
    return () => clearInterval(t);
  }, []);

  // Poll real fal status.
  useEffect(() => {
    if (!userId || !orderId) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const r = await v2Service.getFalStatus({ userId, orderId });
        if (cancelled) return;
        if (r.done || r.status === 'completed' || r.status === 'failed' || r.status === 'preview') {
          setStatus('done');
          return; // parent will swap to the finished video / failure state
        }
        setStatus((r.status as 'queued' | 'in_progress' | 'rendered') || 'in_progress');
        setQueuePos(typeof r.queue_position === 'number' ? r.queue_position : null);
        setFalPct(typeof r.percent === 'number' ? r.percent : null);
      } catch { /* keep showing the estimate */ }
    };
    void poll();
    // Pause network polling while the tab is backgrounded (resumes on the next tick when the
    // user returns) — the 250ms local clock keeps the estimate bar smooth either way.
    const id = setInterval(() => { if (!document.hidden) void poll(); }, 3000);
    return () => { cancelled = true; clearInterval(id); };
  }, [userId, orderId]);

  // Anchor the estimate clock to when generation actually started (the order's
  // created_date), so reopening the project mid-render RESUMES the bar instead
  // of restarting at 0. Backend writes naive UTC → append 'Z' so the browser
  // doesn't read it as local time. Falls back to this mount's time.
  const startMs = (() => {
    if (!startedAt) return mounted.current;
    const s = /[zZ]|[+-]\d\d:?\d\d$/.test(startedAt) ? startedAt : `${startedAt}Z`;
    const t = Date.parse(s);
    return Number.isNaN(t) ? mounted.current : t;
  })();
  const elapsed = Math.max(0, (Date.now() - startMs) / 1000);
  const estSec = estimateSec(falModel);
  const timePct = Math.min(95, (elapsed / estSec) * 100);
  const etaHint =
    estSec <= 75 ? t('falGenerating.etaUnderMinute')
    : estSec <= 200 ? t('falGenerating.etaCoupleMinutes')
    : t('falGenerating.etaSeveralMinutes');
  // Prefer fal's real percent; else the time estimate. 'rendered' = fal finished,
  // we're just waiting on the webhook to re-host → pin near the top.
  const pct = status === 'rendered'
    ? 99
    : falPct != null
      ? Math.max(falPct, status === 'in_progress' ? 5 : 0)
      : timePct;

  const label =
    status === 'queued'
      ? (queuePos != null && queuePos > 0 ? t('falGenerating.inQueuePosition', { position: queuePos }) : t('falGenerating.inQueue'))
      : status === 'rendered'
        ? t('falGenerating.finishingUp')
        : t('falGenerating.renderingYourVideo');

  return (
    <div className="relative w-full aspect-video rounded-2xl overflow-hidden bg-black border border-glass-border animate-fade-in">
      {/* Clean branded gradient backdrop (no blur image for premium) */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/45 via-accent/30 to-primary/25 animate-pulse" style={{ filter: 'blur(6px)', transform: 'scale(1.1)' }} />
      <div className="absolute inset-0 bg-black/40" />

      <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-center px-6">
        <span className="w-14 h-14 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center">
          <Loader2 className="w-7 h-7 text-white animate-spin" />
        </span>
        <p className="text-lg font-bold text-white drop-shadow-md">{label}</p>
        <p className="text-xs text-white/70 max-w-xs">
          {etaHint} {t('falGenerating.canLeaveTab')}
        </p>
      </div>

      {/* % badge */}
      <span className="absolute top-3 right-3 px-3 py-1.5 rounded-full bg-black/55 backdrop-blur-sm text-sm font-extrabold text-white tabular-nums shadow-lg">
        {Math.round(pct)}%
      </span>

      {/* Progress bar */}
      <div className="absolute bottom-0 inset-x-0 h-3 bg-black/40">
        <div
          className={cn('relative h-full bg-gradient-to-r from-primary via-accent to-primary bg-[length:200%_auto] animate-shimmer shadow-[0_0_16px_rgba(139,92,246,0.7)] transition-[width] duration-500 ease-out')}
          style={{ width: `${pct}%` }}
        >
          <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent bg-[length:200%_100%] animate-shimmer" />
          <span className="absolute right-0 top-0 h-full w-1.5 bg-white/80 blur-[2px]" />
        </div>
      </div>
    </div>
  );
}
